// =============================================================================
// src/core/marker-factory.ts — Constroi o HTML/divIcon dos marcadores do mapa.
//
// Proveniencia: assets/unified-app.js MapController.addMarkerLayer (linhas
//   543-571), o trecho que monta o HTML inline de cada marcador (cluster / pulse /
//   dot). Extraido do MapController para manter map-controller.ts < 500 linhas e
//   funcoes < 20 linhas (SRP). Markup/estilos inline (cores, tamanhos, box-shadow,
//   badge) sao paridade visual — NAO simplificar.
//
// Leaflet: usa L.divIcon (verificado via Context7 /leaflet/leaflet, 2026-05-30).
// =============================================================================

import L from 'leaflet';
import type { DivIcon } from 'leaflet';
import type { MapMarkerDef } from '../app/types';

/** Cor padrao do pulso quando o marcador pulsante nao define `color` (legado). */
const DEFAULT_PULSE_COLOR = '#E24B4A';

/** HTML de um marcador agrupado (size === 'cluster'): bolha com contagem +N. */
function clusterMarkerHtml(def: MapMarkerDef): string {
  return (
    '<div style="position:relative;width:28px;height:28px;">' +
    `<div style="width:28px;height:28px;border-radius:50%;background:${def.color};opacity:0.85;display:flex;align-items:center;justify-content:center;border:2px solid white;">` +
    `<span style="font-size:10px;font-weight:600;color:white">+${def.count}</span></div></div>`
  );
}

/** Badge superior-direito opcional (numero) — compartilhado pelo marcador pulsante. */
function pulseBadgeHtml(badge: number | undefined): string {
  if (!badge) return '';
  return (
    '<div style="position:absolute;top:-4px;right:-6px;background:white;color:#1F1F1F;border-radius:999px;font-size:9px;font-weight:500;padding:1px 5px;border:0.5px solid rgba(0,0,0,0.1);min-width:16px;text-align:center">' +
    `${badge}</div>`
  );
}

/** HTML de um marcador pulsante (pulse === true): aro animado + icone + badge. */
function pulseMarkerHtml(def: MapMarkerDef): string {
  const pulseColor = def.color || DEFAULT_PULSE_COLOR;
  return (
    '<div style="position:relative;width:28px;height:28px;">' +
    `<div class="u-marker-pulse" style="background:${pulseColor}"></div>` +
    `<div style="position:absolute;inset:0;width:28px;height:28px;border-radius:50%;background:${def.color};display:flex;align-items:center;justify-content:center;border:2px solid white;">` +
    `<i class="ti ${def.icon}" style="font-size:13px;color:white"></i></div>` +
    pulseBadgeHtml(def.badge) +
    '</div>'
  );
}

/** Rotulo flutuante opcional abaixo do ponto — usado pelo marcador-dot. */
function dotLabelHtml(label: string | undefined): string {
  if (!label) return '';
  return (
    '<div style="position:absolute;top:100%;left:50%;transform:translateX(-50%);background:white;border:0.5px solid rgba(0,0,0,0.12);border-radius:4px;padding:2px 6px;font-size:9px;font-weight:500;white-space:nowrap;margin-top:2px;color:#1F1F1F">' +
    `${label}</div>`
  );
}

/** HTML de um marcador simples (dot): bolha colorida com icone (+ rotulo opcional). */
function dotMarkerHtml(def: MapMarkerDef): string {
  const dotSize = def.size === 'major' ? 30 : 22;
  const iconSize = def.size === 'major' ? 13 : 11;
  return (
    '<div style="position:relative;">' +
    `<div style="width:${dotSize}px;height:${dotSize}px;border-radius:50%;background:${def.color};display:flex;align-items:center;justify-content:center;border:2px solid white;box-shadow:0 1px 4px rgba(0,0,0,0.2);">` +
    `<i class="ti ${def.icon}" style="font-size:${iconSize}px;color:white"></i></div>` +
    dotLabelHtml(def.label) +
    '</div>'
  );
}

/** Escolhe o HTML conforme o tipo do marcador (cluster > pulse > dot), como o legado. */
function markerHtmlOf(def: MapMarkerDef): string {
  if (def.size === 'cluster') return clusterMarkerHtml(def);
  if (def.pulse) return pulseMarkerHtml(def);
  return dotMarkerHtml(def);
}

/** Tamanho do icone (px) por tipo — replica `sz` do addMarkerLayer original. */
function markerSizeOf(def: MapMarkerDef): number {
  if (def.size === 'major') return 30;
  return def.pulse ? 28 : 22;
}

/**
 * Constroi o L.divIcon de um marcador (HTML + iconSize + iconAnchor centralizado).
 * Reune as funcoes acima; consumido por MapController.addMarkerLayer.
 */
export function buildMarkerDivIcon(def: MapMarkerDef): DivIcon {
  const size = markerSizeOf(def);
  return L.divIcon({
    className: '',
    html: markerHtmlOf(def),
    iconSize: [size, size],
    iconAnchor: [size / 2, size / 2],
  });
}
